import { Box, Text, VStack } from '@chakra-ui/react';
import type { SessionGuidance, SessionInfo } from '@web-app/types/session-prep';
import { FC } from 'react';

import { JournalActivityDisclosure } from './JournalActivityDisclosure';
import { SessionPrepCardSkeleton } from './SessionPrepCardSkeleton';

export interface JournalActivityData {
  count: number;
  hasMore: boolean;
}

export interface SessionFocusCardProps {
  error: Error | null;
  sessionInfo: SessionInfo | null;
  sessionGuidance: SessionGuidance | null;
  isLoading: boolean;
  journalActivity?: JournalActivityData | null;
  onJournalClick?: () => void;
}

/**
 * Card displaying session focus and tasks for the current session.
 * Shows framework guidance based on session number.
 */
export const SessionFocusCard: FC<SessionFocusCardProps> = ({
  error,
  sessionGuidance,
  isLoading,
  journalActivity,
  onJournalClick,
}) => {
  if (isLoading) {
    return <SessionPrepCardSkeleton rows={2} />;
  }

  const renderContent = () => {
    if (error) {
      return (
        <Text fontSize="sm" color="text.muted" py={4}>
          Failed to load session focus.
        </Text>
      );
    }

    if (!sessionGuidance) {
      return (
        <Text fontSize="sm" color="text.muted" py={4}>
          No session focus available
        </Text>
      );
    }

    return (
      <>
        <Text fontWeight="600" fontSize="md" mb={1}>
          {sessionGuidance.focusLabel}
        </Text>
        {sessionGuidance.guidance && (
          <Text fontSize="sm" color="text.default">
            {sessionGuidance.guidance}
          </Text>
        )}
      </>
    );
  };

  return (
    <Box data-testid="session-focus-card">
      <VStack spacing={4} align="stretch">
        <Box>
          <Text
            fontSize="xs"
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing="0.5px"
            color="text.subtle"
            mb={3}
          >
            Session Focus
          </Text>

          {renderContent()}
        </Box>

        {journalActivity && journalActivity.count > 0 && (
          <JournalActivityDisclosure
            count={journalActivity.count}
            hasMore={journalActivity.hasMore}
            onClick={onJournalClick}
          />
        )}
      </VStack>
    </Box>
  );
};

SessionFocusCard.displayName = 'SessionFocusCard';
